/**
 * Renders a `Field[]` (the body of a SettingsPanel) into themed rows by kind.
 * Pure presentation: reads values from a flat `Record<string, unknown>` and
 * reports edits via `onChange(key, value)`. The scope engine owns where those
 * values live + how they persist. Reuses the Fibonacci dialog's row skin so
 * every settings body looks identical.
 */
import { ColorPicker } from "@/components/ColorPicker/ColorPicker";
import { formatColor, parseColor } from "@/utils/colorWithAlpha";
import type { LineStyleName } from "@/state/slices/uiSlice";
import { LineStyleSelect } from "./LineStyleSelect";
import type {
  ColorField,
  Field,
  HeaderField,
  NumberField,
  SegmentedField,
  SelectField,
  StyleField,
  ToggleField,
} from "./registry";
import styles from "./panel.module.css";

interface Props {
  fields: Field[];
  value: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
}

export function FieldList({ fields, value, onChange }: Props) {
  return (
    <>
      {fields.map((f) => (
        <FieldRow key={f.key} field={f} value={value} onChange={onChange} />
      ))}
    </>
  );
}

function FieldRow({
  field,
  value,
  onChange,
}: {
  field: Field;
  value: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
}) {
  switch (field.kind) {
    case "header":
      return <HeaderRow field={field} />;
    case "color":
      return <ColorRow field={field} value={value} onChange={onChange} />;
    case "number":
      return <NumberRow field={field} value={value} onChange={onChange} />;
    case "style":
      return <StyleRow field={field} value={value} onChange={onChange} />;
    case "toggle":
      return <ToggleRow field={field} value={value} onChange={onChange} />;
    case "select":
      return <SelectRow field={field} value={value} onChange={onChange} />;
    case "segmented":
      return <SegmentedRow field={field} value={value} onChange={onChange} />;
  }
}

function HeaderRow({ field }: { field: HeaderField }) {
  return <p className={styles.sectionHeader}>{field.label}</p>;
}

function ColorRow({
  field,
  value,
  onChange,
}: {
  field: ColorField;
  value: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
}) {
  // matchKey absent or false → show the picker. When matched, the colour
  // follows another (handled by the renderer), so no picker.
  const matched =
    field.matchKey != null && value[field.matchKey] !== false;
  const current =
    (value[field.key] as string | undefined) ?? field.default ?? "#2962FF";
  const { color: hex, opacity } = parseColor(current);
  return (
    <div className={styles.lineRow}>
      <span className={styles.lineRowLabel}>{field.label}</span>
      {field.matchKey != null && (
        <label className={styles.inlineCheck}>
          <input
            type="checkbox"
            checked={matched}
            onChange={(e) => onChange(field.matchKey as string, e.target.checked)}
          />
          {field.matchLabel ?? "Match"}
        </label>
      )}
      {!matched && (
        <ColorPicker
          value={hex}
          opacity={opacity}
          onChange={(c, o) => onChange(field.key, formatColor(c, o))}
          ariaLabel={field.label}
        />
      )}
    </div>
  );
}

function NumberRow({
  field,
  value,
  onChange,
}: {
  field: NumberField;
  value: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
}) {
  const current = (value[field.key] as number | undefined) ?? field.default ?? field.min;
  const step = field.step ?? 1;
  const options: number[] = [];
  for (let n = field.min; n <= field.max; n += step) options.push(n);
  return (
    <div className={styles.lineRow}>
      <span className={styles.lineRowLabel}>{field.label}</span>
      <select
        className={styles.scopeSelect}
        value={current}
        onChange={(e) => onChange(field.key, Number(e.target.value))}
      >
        {options.map((n) => (
          <option key={n} value={n}>
            {n}
            {field.unit ? ` ${field.unit}` : ""}
          </option>
        ))}
      </select>
    </div>
  );
}

function StyleRow({
  field,
  value,
  onChange,
}: {
  field: StyleField;
  value: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
}) {
  const current =
    (value[field.key] as LineStyleName | undefined) ?? field.default ?? "solid";
  return (
    <div className={styles.lineRow}>
      <span className={styles.lineRowLabel}>{field.label}</span>
      <LineStyleSelect
        value={current}
        onChange={(s) => onChange(field.key, s)}
      />
    </div>
  );
}

function ToggleRow({
  field,
  value,
  onChange,
}: {
  field: ToggleField;
  value: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
}) {
  const current = (value[field.key] as boolean | undefined) ?? field.default ?? false;
  return (
    <label className={styles.lineRow} style={{ cursor: "pointer" }}>
      <span className={styles.lineRowLabel}>{field.label}</span>
      <input
        type="checkbox"
        checked={current}
        onChange={(e) => onChange(field.key, e.target.checked)}
      />
    </label>
  );
}

function SelectRow({
  field,
  value,
  onChange,
}: {
  field: SelectField;
  value: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
}) {
  const current =
    (value[field.key] as string | undefined) ?? field.default ?? field.options[0]?.value;
  return (
    <div className={styles.lineRow}>
      <span className={styles.lineRowLabel}>{field.label}</span>
      <select
        className={styles.scopeSelect}
        value={current}
        onChange={(e) => onChange(field.key, e.target.value)}
      >
        {field.options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function SegmentedRow({
  field,
  value,
  onChange,
}: {
  field: SegmentedField;
  value: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
}) {
  const current =
    (value[field.key] as string | undefined) ??
    field.default ??
    field.options[0]?.value;
  return (
    <div className={styles.lineRow}>
      <span className={styles.lineRowLabel}>{field.label}</span>
      <div className={styles.segmented} role="radiogroup">
        {field.options.map((o) => (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={current === o.value}
            className={`${styles.segment} ${current === o.value ? styles.segmentActive : ""}`}
            onClick={() => onChange(field.key, o.value)}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}
